import { FC, useContext, useMemo } from 'react';

import { Label, Separator } from '@smartshell/ui-kit';
import cn from 'classnames';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { assumeDuration, formatSum } from 'utils';

import { TEntity, TTariff } from '../interfaces';
import s from './Preview.module.scss';

type TSelectedEntity = TEntity & {
  title: string;
  basePrice: number;
};
type TPreviewProps = {
  title: string;
  group?: string;
  tariff?: TTariff;
  goods: Array<TSelectedEntity>;
  services: Array<TSelectedEntity>;
  useDiscounts: boolean;
};
export const Preview: FC<TPreviewProps> = ({
  tariff,
  title,
  group,
  goods,
  services,
  useDiscounts,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);

  const totals = useMemo(() => {
    const tariffPrice = tariff?.price ?? 0;

    const goodsTotal = goods.reduce(
      (acc, good) => ({
        base: acc.base + good.basePrice * good.amount,
        final: acc.final + good.price * good.amount,
      }),
      { base: 0, final: 0 }
    );
    const servicesTotal = services.reduce(
      (acc, service) => ({
        base: acc.base + service.basePrice * service.amount,
        final: acc.final + service.price * service.amount,
      }),
      { base: 0, final: 0 }
    );

    return {
      base: goodsTotal.base + servicesTotal.base + tariffPrice,
      final: goodsTotal.final + servicesTotal.final + tariffPrice,
    };
  }, [goods, services, tariff]);

  return (
    <div className={s.container}>
      <div className={s.block}>
        <span className={s.title}>{title || '—'}</span>
        <span className={s.gray}>{group ?? t('hosts.allGroups')}</span>
        {useDiscounts && <Label iconName="PercentIcon" />}
      </div>
      {tariff && (
        <div className={s.item}>
          <span>{tariff.title}</span>
          <div className={cn(s.gray, s.bottom)}>
            <span>
              {formatSum(tariff.price, currency, {
                maximumFractionDigits: 2,
              })}
            </span>
            {tariff.isPackage
              ? t('until', tariff.duration)
              : assumeDuration(Number(tariff.duration), t)?.title}
          </div>
        </div>
      )}
      {goods.map((good) => (
        <div key={good.entity_id} className={s.item}>
          <span>{good.title}</span>
          <div className={cn(s.gray, s.bottom)}>
            <span>
              {formatSum(good.price, currency, {
                maximumFractionDigits: 2,
              })}
            </span>
            х
            <span>
              {good.amount} {t('pcs')}
            </span>
          </div>
        </div>
      ))}
      {services.map((service) => (
        <div key={service.entity_id} className={s.item}>
          <span>{service.title}</span>
          <div className={cn(s.gray, s.bottom)}>
            <span>
              {formatSum(service.price, currency, {
                maximumFractionDigits: 2,
              })}
            </span>
            х
            <span>
              {service.amount} {t('pcs')}
            </span>
          </div>
        </div>
      ))}
      <Separator />
      <div className={s.block}>
        <span className={s.gray}>{t('combo.price.base')}</span>
        <span className={s.basePrice}>
          {totals.base
            ? formatSum(totals.base, currency, { maximumFractionDigits: 2 })
            : '—'}
        </span>
      </div>
      <div className={s.block}>
        <span className={s.gray}>{t('combo.price')}</span>
        {totals.final
          ? formatSum(totals.final, currency, { maximumFractionDigits: 2 })
          : '—'}
      </div>
    </div>
  );
};
